import { v4 as uuidv4 } from "uuid";
import {
	FIELD_TYPE_SUPPORTS_PLACEHOLDER,
	FieldType,
	FormField,
} from "../types/formTypes";

export const createNewField = (type: FieldType): FormField => {
	const newField: FormField = {
		id: uuidv4(),
		type,
		label: "",
		required: false,
	};

	if (type === "title") {
		newField.label = "Abschnittstitel";
		newField.required = false;
	} else if (type === "odontogram") {
		newField.label = "Zähne auswählen";
		newField.required = false;
		newField.defaultValue = [];
		newField.odontogramFields = []; // Initialize with empty odontogram fields array
	} else if (type === "appointment") {
		newField.label = "Termin";
		newField.required = false;
		newField.appointmentFields = [];
	} else {
		newField.label = "Neue Frage";
		if (
			type === "select" ||
			type === "radio" ||
			type === "checkbox" ||
			type === "chips"
		) {
			newField.options = ["Option 1", "Option 2"];
		}
	}

	return newField;
};

export const duplicateField = (field: FormField): FormField => {
	return {
		...field,
		id: uuidv4(),
	};
};

export const handleFieldTypeChange = (
	field: FormField,
	newType: FieldType
): FormField => {
	// First copy over all previous properties
	// And below update as needed based on previous and new type
	const updatedField: FormField = {
		...field,
		type: newType,
	};

	const newTypeNeedsOptions = [
		"select",
		"radio",
		"checkbox",
		"chips",
	].includes(newType);
	const oldTypeHadOptions = ["select", "radio", "checkbox", "chips"].includes(
		field.type
	);

	if (newTypeNeedsOptions && !oldTypeHadOptions) {
		updatedField.options = ["Option 1", "Option 2"];
	} else if (!newTypeNeedsOptions && oldTypeHadOptions) {
		delete updatedField.options;
	}
	// If both are options-based, we can just keep the existing options

	// allowOther only applies to single-selection types (radio, chips)
	if (newType !== "radio" && newType !== "chips") {
		delete updatedField.allowOther;
	}

	// multi only applies to chips
	if (newType !== "chips") {
		delete updatedField.multi;
	}

	// Handle odontogram-specific fields
	if (newType === "odontogram") {
		updatedField.odontogramFields = field.odontogramFields || [];
		updatedField.defaultValue = [];
	} else if (field.type === "odontogram") {
		// Switching away from odontogram
		delete updatedField.odontogramFields;
		delete updatedField.defaultValue;
	}

	// Handle appointment-specific fields
	if (newType === "appointment") {
		updatedField.appointmentFields = field.appointmentFields || [];
	} else if (field.type === "appointment") {
		delete updatedField.appointmentFields;
		delete updatedField.maxAppointments;
	}

	// If new field doesn't support placeholder, delete it
	if (!FIELD_TYPE_SUPPORTS_PLACEHOLDER(newType)) {
		delete updatedField.placeholder;
	}

	return updatedField;
};

// Helper function to create a new dropdown field for Odontogram
export const createNewOdontogramField = (
	type: FieldType = "select"
): FormField => {
	const field: FormField = {
		id: uuidv4(),
		type,
		label: "Neue Information",
		required: false,
	};
	if (type === "select") {
		field.options = ["Option 1", "Option 2"];
	} else if (type === "text") {
		field.placeholder = "Feld für kurze Antworten";
	} else if (type === "textarea") {
		field.placeholder = "Feld für lange Antworten";
	}

	return field;
};

export const createNewAppointmentField = (
	type: FieldType = "date"
): FormField => {
	const labelMap: Record<string, string> = {
		date: "Datum",
		time: "Uhrzeit",
		text: "Neue Frage",
		textarea: "Textfeld",
		select: "Auswahl",
		radio: "Einzelauswahl",
		checkbox: "Mehrfachauswahl",
		file: "Datei",
	};

	const field: FormField = {
		id: uuidv4(),
		type,
		label: labelMap[type] || "Information",
		required: false,
	};

	if (type === "select" || type === "radio" || type === "checkbox") {
		field.options = ["Option 1", "Option 2"];
	}
	return field;
};
